import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquarePlus } from "lucide-react";
import { useAppDispatch } from "../../app/hooks";
import { FeedbackType } from "../../features/feedback/enums/FeedbackType";
import { submitPortalFeedback } from "../../features/portal/slice-extensions/portal-feedback-slice-extension";

/** Mirrors the backend FeedbackEntry.MessageMaxLength. */
export const FEEDBACK_MAX_LENGTH = 4000;

interface Props {
  /** The shown member (self or ward); the note is recorded for the signed-in member. */
  memberId: string;
}

type Outcome = "sent" | "failed" | null;

/** "Tell us what's missing" (US-99): a wish, a problem or anything else, sent to Timeplanned. */
const PortalFeedbackCard = ({ memberId }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [type, setType] = useState<FeedbackType>(FeedbackType.Wish);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(null);

  const kinds = [
    { value: FeedbackType.Wish, label: t("feedback.kind.wish") },
    { value: FeedbackType.Issue, label: t("feedback.kind.issue") },
    { value: FeedbackType.Other, label: t("feedback.kind.other") },
  ];

  const send = async () => {
    setBusy(true);
    setOutcome(null);
    try {
      await dispatch(submitPortalFeedback({ memberId, type, message: message.trim() })).unwrap();
      setMessage("");
      setOutcome("sent");
    } catch {
      setOutcome("failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10">
      <h2 className="flex items-center gap-2 text-base font-semibold text-grey-20 dark:text-white">
        <MessageSquarePlus size={20} />
        {t("feedback.title")}
      </h2>
      <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label={t("feedback.modal.typeLabel")}>
        {kinds.map((kind) => (
          <button
            key={kind.value}
            type="button"
            role="radio"
            aria-checked={type === kind.value}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${
              type === kind.value
                ? "bg-primary-95 text-primary-40 dark:bg-primary-30 dark:text-primary-90"
                : "text-grey-40 hover:bg-grey-95 dark:text-grey-70 dark:hover:bg-primary-20"
            }`}
            onClick={() => setType(kind.value)}
          >
            {kind.label}
          </button>
        ))}
      </div>
      <textarea
        className="input mt-3 min-h-[120px]"
        aria-label={t("feedback.title")}
        value={message}
        maxLength={FEEDBACK_MAX_LENGTH}
        placeholder={t("feedback.placeholder")}
        onChange={(event) => setMessage(event.target.value)}
      />
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="text-xs text-grey-50 dark:text-grey-60">
          {message.length} / {FEEDBACK_MAX_LENGTH}
        </span>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || message.trim().length === 0}
          onClick={send}
        >
          {t("feedback.send")}
        </button>
      </div>
      {outcome === "sent" && (
        <p role="status" className="mt-3 rounded-2xl bg-green-100 p-3 text-sm text-green-800 dark:bg-green-900/30 dark:text-green-300">
          {t("feedback.thanks")}
        </p>
      )}
      {outcome === "failed" && <p role="alert" className="field-error mt-3">{t("feedback.modal.failed")}</p>}
    </div>
  );
};

export default PortalFeedbackCard;
